import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Stack,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import type { RFQItem, RFQSupplier } from '../types';
import { rfqService } from '../services/rfqService';
import { dataService } from '../services/dataService';
import { colors } from '../theme';

// ============================================================================
// Unidades disponibles
// ============================================================================

const UNIT_OPTIONS = [
  'Unidades',
  'Kg',
  'Litros',
  'Metros',
  'Toneladas',
  'Galones',
  'Pies',
  'Juegos',
  'Servicio',
  'Global',
];

// ============================================================================
// Interfaz de item del formulario (con validación)
// ============================================================================

interface FormItem {
  id: string;
  category: string;
  description: string;
  quantity: string; // string para el input, se convierte a number al guardar
  unit: string;
  specs: string;
}

const createEmptyItem = (): FormItem => ({
  id: crypto.randomUUID(),
  category: '',
  description: '',
  quantity: '',
  unit: 'Unidades',
  specs: '',
});

const RFQCreate: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [deadline, setDeadline] = useState('');
  const [items, setItems] = useState<FormItem[]>([createEmptyItem()]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);

  // Data
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableSuppliers, setAvailableSuppliers] = useState<string[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // UI
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // =========================================================================
  // Carga de datos
  // =========================================================================

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        await dataService.loadPurchaseOrders('/00_Informacion utilizada/PurchaseOrders.csv');
        setAvailableCategories(dataService.getCategories());
        setAvailableSuppliers(dataService.getSuppliers());
      } catch (error) {
        console.error('[RFQCreate] Error cargando datos:', error);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, []);

  // =========================================================================
  // Mínimo de fecha: mañana
  // =========================================================================

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  // =========================================================================
  // Items handlers
  // =========================================================================

  const handleAddItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const handleRemoveItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const handleItemChange = (id: string, field: keyof FormItem, value: string) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // =========================================================================
  // Validación
  // =========================================================================

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'El título es obligatorio';
    }

    if (!deadline) {
      newErrors.deadline = 'La fecha límite es obligatoria';
    }

    if (selectedSuppliers.length === 0) {
      newErrors.suppliers = 'Seleccione al menos un proveedor';
    }

    // Validar items
    let hasValidItem = false;
    items.forEach((item, index) => {
      if (!item.category) {
        newErrors[`item_${index}_category`] = 'Seleccione una categoría';
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`item_${index}_quantity`] = 'Ingrese una cantidad válida';
      }
      if (item.category && item.quantity && parseFloat(item.quantity) > 0) {
        hasValidItem = true;
      }
    });

    if (!hasValidItem) {
      newErrors.items = 'Agregue al menos un item con categoría y cantidad';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =========================================================================
  // Submit
  // =========================================================================

  const handleSubmit = (sendImmediately: boolean) => {
    if (!validate()) return;

    // Convertir items del formulario a tipo RFQItem
    const rfqItems: RFQItem[] = items
      .filter(item => item.category && item.quantity)
      .map(item => ({
        id: crypto.randomUUID(),
        category: item.category,
        description: item.description,
        quantity: parseFloat(item.quantity),
        unit: item.unit,
        specs: item.specs || undefined,
      }));

    // Construir suppliers con email mock
    const rfqSuppliers: RFQSupplier[] = selectedSuppliers.map(name => ({
      supplierId: `supplier-${name.toLowerCase().replace(/\s+/g, '-')}`,
      supplierName: name,
      contactEmail: `compras@${name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 15)}.com`,
      hasResponded: false,
    }));

    // Categoría principal = primera categoría
    const mainCategory = rfqItems[0]?.category || '';

    try {
      const newRfq = rfqService.create({
        title: title.trim(),
        items: rfqItems,
        suppliers: rfqSuppliers,
        deadline: new Date(deadline + 'T23:59:59').toISOString(),
        notes: notes.trim() || undefined,
        mainCategory,
      });

      if (sendImmediately) {
        rfqService.send(newRfq.id);
        setSnackbar({
          open: true,
          message: `✉️ Compulsa ${newRfq.number} enviada a ${rfqSuppliers.length} proveedor${rfqSuppliers.length > 1 ? 'es' : ''}`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `✅ Compulsa ${newRfq.number} guardada como borrador`,
          severity: 'success',
        });
      }

      // Navegar después de un breve delay para que el usuario vea el snackbar
      setTimeout(() => navigate('/rfq'), 1200);
    } catch (error) {
      console.error('[RFQCreate] Error creando RFQ:', error);
      setSnackbar({
        open: true,
        message: 'Error al crear la compulsa',
        severity: 'error',
      });
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title="Volver a la lista">
          <IconButton onClick={() => navigate('/rfq')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Nueva Compulsa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cree una solicitud de cotización para proveedores
          </Typography>
        </div>
      </Box>

      {/* Sección 1: Datos del RFQ */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Datos de la Compulsa
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Título"
              placeholder="Ej: Compulsa Lubricantes - Flota Remolcadores Q2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Fecha Límite"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate }}
              error={!!errors.deadline}
              helperText={errors.deadline || 'Fecha límite para recibir cotizaciones'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notas Internas"
              placeholder="Observaciones internas que no se envían al proveedor..."
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Sección 2: Items Solicitados */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Items Solicitados
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Agregar Item
          </Button>
        </Box>

        {errors.items && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.items}
          </Alert>
        )}

        <Stack spacing={2}>
          {items.map((item, index) => (
            <Card key={item.id} variant="outlined" sx={{ borderColor: errors[`item_${index}_category`] || errors[`item_${index}_quantity`] ? colors.error.main : undefined }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Item {index + 1}
                  </Typography>
                  {items.length > 1 && (
                    <Tooltip title="Eliminar item">
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={availableCategories}
                      value={item.category || null}
                      onChange={(_, newValue) => handleItemChange(item.id, 'category', newValue || '')}
                      loading={loadingData}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Categoría *"
                          placeholder="Buscar categoría..."
                          size="small"
                          error={!!errors[`item_${index}_category`]}
                          helperText={errors[`item_${index}_category`]}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Descripción"
                      placeholder="Detalle del producto/servicio requerido"
                      value={item.description}
                      onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cantidad *"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      error={!!errors[`item_${index}_quantity`]}
                      helperText={errors[`item_${index}_quantity`]}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Unidad"
                      value={item.unit}
                      onChange={(e) => handleItemChange(item.id, 'unit', e.target.value)}
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <MenuItem key={u} value={u}>{u}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Especificaciones Técnicas"
                      placeholder="Normas, marcas requeridas, materiales, tolerancias..."
                      multiline
                      rows={2}
                      value={item.specs}
                      onChange={(e) => handleItemChange(item.id, 'specs', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Paper>

      {/* Sección 3: Selección de Proveedores */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Proveedores Destinatarios
        </Typography>

        <Autocomplete
          multiple
          options={availableSuppliers}
          value={selectedSuppliers}
          onChange={(_, newValue) => setSelectedSuppliers(newValue)}
          loading={loadingData}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Seleccionar Proveedores"
              placeholder={selectedSuppliers.length === 0 ? 'Buscar proveedores...' : ''}
              error={!!errors.suppliers}
              helperText={errors.suppliers || `Seleccione los proveedores a los que se enviará esta compulsa (${availableSuppliers.length} disponibles)`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
                sx={{ fontWeight: 500 }}
              />
            ))
          }
        />

        {selectedSuppliers.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedSuppliers.length} proveedor{selectedSuppliers.length > 1 ? 'es' : ''} seleccionado{selectedSuppliers.length > 1 ? 's' : ''}
          </Typography>
        )}
      </Paper>

      {/* Footer con botones */}
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/rfq')}
        >
          Cancelar
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={() => handleSubmit(false)}
        >
          Guardar Borrador
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => handleSubmit(true)}
          sx={{
            bgcolor: colors.brand.red,
            '&:hover': { bgcolor: colors.primary.dark },
          }}
        >
          Guardar y Enviar
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RFQCreate;
